import { useState } from 'react';
import EmpathySection from './components/EmpathySection';
import BenefitsSection from './components/BenefitsSection';
import SolutionSection from './components/SolutionSection';
import ConversionForm from './components/ConversionForm';
import { useGA4ViewEvent, useGA4ClickEvent } from '../../hooks/useGA4';

export default function HomePage() {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const heroRef = useGA4ViewEvent('nijikango04_view_hero');
  const midCtaRef = useGA4ViewEvent('nijikango04_view_mid_cta');
  const bottomCtaRef = useGA4ViewEvent('nijikango04_view_bottom_cta');
  const footerRef = useGA4ViewEvent('nijikango04_view_footer');

  const handleCtaClick = useGA4ClickEvent('nijikango_click_1');

  const openForm = () => {
    handleCtaClick();
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  return (
    <div className="min-h-screen bg-white w-full max-w-[480px] mx-auto overflow-x-hidden">
      {/* Header - always white */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-[480px] mx-auto px-4 py-3 flex justify-between items-center">
          <a
            href="https://nijikango.co.jp/"
            target="_blank"
            rel="noopener noreferrer"
            className="cursor-pointer"
          >
            <img
              src="https://static.readdy.ai/image/2eeb7004bec3dab6605078c5e30f883b/03d84a07dc69ca97a2487f53039402b6.jpeg"
              alt="ロゴ"
              className="h-5"
            />
          </a>
          <span className="text-[10px] font-medium whitespace-nowrap text-pink-600">
            厚生労働大臣許可番号：13-ユ-317878
          </span>
        </div>
      </header>

      {/* Hero Section */}
      <section ref={heroRef} id="section-hero" className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/2eeb7004bec3dab6605078c5e30f883b/ceb96b9268cc6fdb4e531960ae700049.jpeg"
            alt="看護師"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-white"></div>
        </div>

        <div className="relative z-10 px-5 pt-52 pb-12">
          <p className="text-white text-xs font-bold mb-3 tracking-wider drop-shadow-lg">
            ＼ 30秒で完了 ／
          </p>
          <h1 className="text-[26px] font-bold text-white leading-[1.5] mb-4 drop-shadow-lg">
            夜勤、そろそろ限界…<br />
            と思っている<span className="text-pink-300">あなた</span>へ。
          </h1>
          <p className="text-[13px] text-white leading-[1.9] mb-6 drop-shadow-md">
            「お給料を下げずに夜勤を減らす」働き方を、<br />
            データに基づいた適性診断で<br />
            見つけませんか？
          </p>
          <button
            id="cta-hero"
            data-ga4="nijikango_click_1"
            onClick={openForm}
            className="w-full bg-pink-500 text-white py-4 rounded-full text-[15px] font-bold hover:bg-pink-600 active:bg-pink-700 transition-all shadow-lg shadow-pink-200 whitespace-nowrap cursor-pointer"
          >
            <span className="block text-[11px] font-normal mb-0.5 opacity-90">
              ［30秒で完了］
            </span>
            無料で働き方診断を受ける
          </button>
        </div>
      </section>

      {/* Empathy Section */}
      <EmpathySection />

      {/* Benefits Section */}
      <BenefitsSection />

      {/* Mid-page CTA */}
      <section ref={midCtaRef} id="section-mid-cta" className="py-10 px-5 bg-white">
        <button
          id="cta-mid"
          data-ga4="nijikango_click_1"
          onClick={openForm}
          className="w-full bg-pink-500 text-white py-4 rounded-full text-[15px] font-bold hover:bg-pink-600 active:bg-pink-700 transition-all shadow-lg shadow-pink-200 whitespace-nowrap cursor-pointer"
        >
          <span className="block text-[11px] font-normal mb-0.5 opacity-90">
            ［30秒で完了］
          </span>
          無料で働き方診断を受ける
        </button>
      </section>

      {/* Solution Section */}
      <SolutionSection />

      {/* CTA Section */}
      <section ref={bottomCtaRef} id="section-bottom-cta" className="py-12 px-5 bg-white">
        <h2 className="text-[22px] font-bold text-center text-gray-900 mb-3 leading-[1.5]">
          あなたの「働き方改革」は、<br />
          ここから始まります。
        </h2>
        <p className="text-center text-[13px] text-gray-500 mb-8">
          簡単な質問に答えるだけで最適な働き方をご提案
        </p>
        <button
          id="cta-bottom"
          data-ga4="nijikango_click_1"
          onClick={openForm}
          className="w-full bg-pink-500 text-white py-4 rounded-full text-[15px] font-bold hover:bg-pink-600 active:bg-pink-700 transition-all shadow-lg shadow-pink-200 whitespace-nowrap cursor-pointer"
        >
          <span className="block text-[11px] font-normal mb-0.5 opacity-90">
            ［30秒で完了］
          </span>
          無料で働き方診断を受ける
        </button>
      </section>

      {/* Footer */}
      <footer ref={footerRef} id="section-footer" className="bg-pink-50 py-10 px-5">
        <div className="text-center">
          <div className="flex items-center justify-center gap-5 mb-5 text-[11px] text-gray-500 flex-wrap">
            <a
              href="https://nijikango.co.jp/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-pink-500 transition-colors cursor-pointer"
            >
              運営会社
            </a>
            <a
              href="https://lp.nijikango.co.jp/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-pink-500 transition-colors cursor-pointer"
            >
              プライバシーポリシー
            </a>
            <a
              href="https://lp.nijikango.co.jp/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-pink-500 transition-colors cursor-pointer"
            >
              利用規約
            </a>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">
            &copy; 2025 ニジ看護転職. All rights reserved.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-[10px] text-gray-400 hover:text-pink-400 transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>

      {/* Popup Form */}
      <ConversionForm isOpen={isFormOpen} onClose={closeForm} />
    </div>
  );
}
